// Tipos da alocação do time e do custo acumulado por PI.

export interface AlocacaoPessoa {
  id: string;
  piCodigo: string;
  descricao: string | null;
  percentual: number;
  observacao: string | null;
  /** null quando a pessoa ainda não tem custo cadastrado. */
  custo: number | null;
}

export interface LinhaPessoa {
  userId: string;
  nome: string;
  email: string;
  cargo: string | null;
  squad: string | null;
  ativo: boolean;
  custoMensal: number | null;
  /** Soma dos percentuais do mês. Acima de 100 = sobre-alocada. */
  somaPercentual: number;
  custoNaoAlocado: number | null;
  alocacoes: AlocacaoPessoa[];
}

export interface GradeCusto {
  competencia: string;
  kra: number | null;
  pessoas: LinhaPessoa[];
  totais: {
    pessoas: number;
    semCusto: number;
    sobreAlocadas: number;
    custoTotalPessoas: number;
    custoAlocado: number;
    custoNaoAlocado: number;
    desvioKra: number | null;
  };
}

export type CriterioRateio = 'por_cabeca' | 'igual' | 'carteira';

export const CRITERIOS: { id: CriterioRateio; rotulo: string; explicacao: string }[] = [
  { id: 'por_cabeca', rotulo: 'Por cabeça', explicacao: 'Proporcional às pessoas alocadas (pessoa-mês).' },
  { id: 'igual', rotulo: 'Igual entre os projetos', explicacao: 'Mesmo valor para cada PI com gente alocada.' },
  { id: 'carteira', rotulo: 'Proporcional à carteira', explicacao: 'Proporcional ao saldo do PI no retrato atual.' },
];

/** Um bolo de custo (material, KRA…) que desce para os PIs por um critério. */
export interface PoolCusto {
  id: string;
  nome: string;
  valor: number;
  criterio: CriterioRateio;
  /** Projetos que dividem este custo. Vazio = todos os que têm gente alocada. */
  piCodigos: string[];
  competenciaInicio: string;
  competenciaFim: string;
  observacao: string | null;
}

export interface RateioAplicado extends PoolCusto {
  /** Quanto do bolo efetivamente desceu. Menor que o valor = algo não fechou. */
  distribuido: number;
  alerta: string | null;
}

export interface CustoPorPi {
  piCodigo: string;
  descricao: string | null;
  squad: string | null;
  situacao: string | null;
  saldo: number | null;
  /** Custo por pessoa alocada. */
  custoDireto: number;
  /** Fatia dos bolos rateados. */
  custoRateado: number;
  /** Pessoa-mês acumulado — a base do rateio "por cabeça". */
  cabecas: number;
  /** Faturado do PI, só meses fechados. null quando o PI saiu do retrato atual. */
  faturado: number | null;
  /** Alguém apontado ou custo atribuído a este PI. */
  temEquipe: boolean;
  /** null quando não há custo apurado — sem custo não existe margem. */
  margem: number | null;
  custoAcumulado: number;
  meses: number;
  pessoas: number;
  nomes: string[];
  custoSobreSaldo: number | null;
}

export interface CustoAcumulado {
  porPi: CustoPorPi[];
  rateios: RateioAplicado[];
  serieMensal: {
    competencia: string;
    /** Direto + rateado do mês. */
    custo: number;
    custoDireto: number;
    custoRateado: number;
    cabecas: number;
    acumulado: number;
    pis: number;
    pessoas: number;
    kra: number | null;
  }[];
  totais: {
    custoTotal: number;
    custoDireto: number;
    custoRateado: number;
    /** Faturado só dos PIs que têm custo — é com este que a margem fecha. */
    faturadoDosPis: number;
    margem: number;
    pisForaDoRetrato: number;
    /** Projetos vivos sem ninguém apontado. */
    semEquipe: number;
    /** Meses com gente alocada mas sem aquele custo lançado. */
    lacunas: { nome: string; meses: string[] }[];
    /** Janela em que custo e faturado se sobrepõem — é a que a margem cobre. */
    janela: { inicio: string; fim: string; mesesSemFaturado: number };
    meses: number;
    /** Nomes de quem está alocado mas ainda não tem custo — o total está baixo por eles. */
    pessoasSemCusto: string[];
  };
}

export interface VigenciaCusto {
  id: string;
  vigenciaInicio: string;
  custoMensal: number;
  observacao: string | null;
}

const MESES_CURTOS = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

/** "2026-08" → "ago/26". */
export function competenciaCurta(comp: string): string {
  const [ano, mes] = comp.split('-').map(Number);
  if (!ano || !mes) return comp;
  return `${MESES_CURTOS[mes - 1]}/${String(ano).slice(2)}`;
}

/**
 * Nome legível do projeto a partir da descrição da planilha.
 * "PI_1153_ARCELOR_MITTAL_2023_13-0001" → "ARCELOR MITTAL".
 * Descarta o prefixo PI/número e para no primeiro ano — o que sobra é o cliente.
 */
export function nomeCurtoDoPi(descricao: string | null): string | null {
  if (!descricao) return null;
  const partes = descricao.replace(/^#\w+[\s_-]*/, '').split('_').map((p) => p.trim()).filter(Boolean);
  const uteis: string[] = [];
  for (const parte of partes) {
    if (/^PI$/i.test(parte) || /^\d+$/.test(parte)) {
      // número solto antes do nome é o próprio PI; depois do nome, é ano/série
      if (uteis.length === 0) continue;
      if (/^(19|20)\d{2}$/.test(parte)) break;
      continue;
    }
    if (/^(19|20)\d{2}/.test(parte)) break;
    uteis.push(parte);
  }
  const nome = uteis.join(' ').trim();
  return nome || null;
}

/** Mês corrente em AAAA-MM. */
export function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

/** Os 18 meses em volta de hoje — o suficiente para lançar e revisar. */
export function competenciasProximas(): string[] {
  const hoje = new Date();
  const lista: string[] = [];
  for (let i = -12; i <= 5; i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1);
    lista.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return lista.reverse();
}
